/** Pure helpers for the bulk receipt upload list (status, readiness, FormData). */

/** Per-item lifecycle in the bulk list. */
export type BulkStatus =
    | 'pending'
    | 'analyzing'
    | 'analyzed'
    | 'ready'
    | 'uploaded'
    | 'error';

/** The 6 editable receipt inputs shared by the collapsed preview and the editor. */
export type ReceiptFieldValues = {
    allocationType: '' | 'project_cost' | 'company_expense';
    projectId: string;
    projectNo: number | null;
    category: string;
    subcategory: string;
    merchant: string;
    amount: string;
    transactionDate: string;
    responsibleUserId: string;
    description: string;
};

export type BulkItem = ReceiptFieldValues & {
    localId: string;
    file: File;
    url: string;
    isImage: boolean;
    status: BulkStatus;
    error?: string;
    /** PDFs of proof, appended as pages after the receipt. */
    proofs?: File[];
    /** PDF page-split state; 'failed' items are never uploaded. */
    split?: 'done' | 'failed';
    /** localId of the multi-page PDF this page was split from; only set when it split into >1 page. */
    splitGroup?: string;
};

/** Chip label + MUI color per status. */
export const BULK_STATUS_META: Record<
    BulkStatus,
    { label: string; color: 'default' | 'info' | 'warning' | 'success' | 'primary' | 'error' }
> = {
    pending: { label: 'Pending', color: 'default' },
    analyzing: { label: 'Analyzing', color: 'info' },
    analyzed: { label: 'Analyzed', color: 'warning' },
    ready: { label: 'Ready', color: 'success' },
    uploaded: { label: 'Uploaded', color: 'primary' },
    error: { label: 'Error', color: 'error' },
};

/** Fields required before an item can be ready (empty array = ready to upload). */
export function missingRequired(v: ReceiptFieldValues): string[] {
    const missing: string[] = [];
    if (!v.allocationType) missing.push('allocation type');
    else if (v.allocationType === 'project_cost' && !v.projectId) missing.push('project');
    else if (v.allocationType === 'company_expense' && !v.category) missing.push('category');
    if (!v.responsibleUserId) missing.push('responsible employee');
    if (!v.transactionDate) missing.push('transaction date');
    return missing;
}

export function isReady(v: ReceiptFieldValues): boolean {
    return missingRequired(v).length === 0;
}

/** Status after an analyze finishes (or after a field edit) — ready vs still needs input. */
export function statusAfterInput(v: ReceiptFieldValues): BulkStatus {
    return isReady(v) ? 'ready' : 'analyzed';
}

const notUploaded = (items: BulkItem[]) =>
    items.filter(item => item.status !== 'uploaded' && item.split !== 'failed');

/**
 * Upload is allowed when every not-yet-uploaded item is 'ready' or 'error'
 * (error items keep valid fields, so a retry click stays possible).
 */
export function canUpload(items: BulkItem[]): boolean {
    const pending = notUploaded(items);
    if (!pending.length) return false;
    return pending.every(item => item.status === 'ready' || item.status === 'error');
}

/** One-line reason the Upload button is disabled, or null when it is enabled. */
export function uploadBlockMessage(items: BulkItem[]): string | null {
    const pending = notUploaded(items);
    if (!pending.length || canUpload(items)) return null;
    const analyzing = pending.filter(
        item => item.status === 'pending' || item.status === 'analyzing'
    ).length;
    const needInput = pending.filter(item => item.status === 'analyzed').length;
    const parts: string[] = [];
    if (analyzing) parts.push(`${analyzing} still analyzing`);
    if (needInput) {
        parts.push(
            `${needInput} missing required fields (allocation, project/category, employee, transaction date)`
        );
    }
    return parts.length
        ? `Can't upload yet: ${parts.join('; ')}.`
        : 'Add receipts and fill required fields to upload.';
}

/** Other rows from the same split PDF that can be joined into `sourceId`. */
export function joinCandidates(items: BulkItem[], sourceId: string): BulkItem[] {
    const source = items.find(item => item.localId === sourceId);
    if (!source?.splitGroup) return [];
    return items.filter(
        item =>
            item.localId !== sourceId &&
            item.splitGroup === source.splitGroup &&
            item.status !== 'uploaded' &&
            item.status !== 'analyzing'
    );
}

/**
 * Moves `targetId`'s file and proofs into `sourceId`'s proofs and removes the target.
 * Returns `items` unchanged (same reference) unless the target is a current candidate.
 */
export function joinItems(items: BulkItem[], sourceId: string, targetId: string): BulkItem[] {
    const source = items.find(item => item.localId === sourceId);
    const target = items.find(item => item.localId === targetId);
    if (!source || !target || !joinCandidates(items, sourceId).includes(target)) return items;
    return items
        .filter(item => item.localId !== targetId)
        .map(item =>
            item.localId === sourceId
                ? {
                      ...item,
                      proofs: [...(item.proofs ?? []), target.file, ...(target.proofs ?? [])],
                  }
                : item
        );
}

/** Multipart body for POST /receipts — mirrors buildFormData in receiptUploadQueue.ts. */
export function buildReceiptFormData(item: BulkItem): FormData {
    const form = new FormData();
    form.append('files', item.file);
    for (const proof of item.proofs ?? []) {
        form.append('files', proof);
    }
    form.append('allocation_type', item.allocationType || 'company_expense');
    if (item.allocationType === 'project_cost') {
        if (item.projectId) form.append('project_id', item.projectId);
    } else {
        if (item.category) form.append('category', item.category);
        if (item.subcategory) form.append('subcategory', item.subcategory);
    }
    if (item.merchant) form.append('merchant', item.merchant);
    if (item.amount) form.append('amount', item.amount);
    if (item.transactionDate) form.append('transaction_date', item.transactionDate);
    if (item.responsibleUserId) {
        form.append('responsible_user_id', item.responsibleUserId);
    }
    if (item.description) form.append('description', item.description);
    return form;
}
